import React, { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import {
  UserPlus, Trash2, Edit2, Check, X, Loader2, Shield, FolderKanban,
  User, Key, Power, Building2, RefreshCw
} from "lucide-react";

export default function AdminPanel({ projects, refreshKey }) {
  const { api, isAdmin } = useAuth();
  const [allMarketers, setAllMarketers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState({ email: "", password: "", name: "", projects: [] });
  const [clientForm, setClientForm] = useState({ email: "", password: "", name: "", projects: [] });
  const [showForm, setShowForm] = useState(false);
  const [showClientForm, setShowClientForm] = useState(false);
  const [quickCreate, setQuickCreate] = useState(null);
  const [quickForm, setQuickForm] = useState({ email: "", password: "" });
  const [editing, setEditing] = useState(null);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const fetchAll = async () => {
    const results = await Promise.allSettled([
      api.get("/stats/all-marketers"),
      api.get("/auth/clients"),
    ]);
    if (results[0].status === "fulfilled") {
      setAllMarketers(results[0].value.data);
    } else {
      console.error("Failed to load marketers:", results[0].reason);
    }
    if (results[1].status === "fulfilled") {
      setClients(results[1].value.data);
    } else {
      console.error("Failed to load clients:", results[1].reason);
    }
    if (results[0].status === "rejected" && results[1].status === "rejected") {
      setError("Failed to load data. Click Refresh to retry.");
    }
    setLoaded(true);
  };

  useEffect(() => { fetchAll(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAdmin) return <div className="empty-state">Admin access required</div>;
  if (!loaded) return <div className="admin-section" data-testid="admin-section"><div style={{textAlign:'center',padding:48}}><Loader2 size={24} className="animate-spin" style={{color:'#ff4500',margin:'0 auto 12px'}} /><p style={{color:'#6b7280'}}>Loading admin data...</p></div></div>;

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true); setError(""); setSuccess("");
    try {
      await api.post("/auth/create-marketer", form);
      setSuccess(`Marketer "${form.name}" created with login!`);
      setForm({ email: "", password: "", name: "", projects: [] });
      setShowForm(false);
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    if (!clientForm.projects.length) { setError("Client must have at least one project"); return; }
    setLoading(true); setError(""); setSuccess("");
    try {
      await api.post("/auth/create-client", clientForm);
      setSuccess(`Client "${clientForm.name}" created!`);
      setClientForm({ email: "", password: "", name: "", projects: [] });
      setShowClientForm(false);
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const handleQuickCreate = async (marketerName) => {
    if (!quickForm.email || !quickForm.password) return;
    setLoading(true); setError(""); setSuccess("");
    try {
      await api.post("/auth/create-marketer", { name: marketerName, email: quickForm.email, password: quickForm.password, projects: [] });
      setSuccess(`Login created for "${marketerName}"!`);
      setQuickCreate(null);
      setQuickForm({ email: "", password: "" });
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed");
    } finally { setLoading(false); }
  };

  const handleUpdate = async (uid) => {
    try {
      await api.patch(`/auth/marketers/${uid}`, editData);
      setEditing(null);
      setSuccess("Updated!");
      fetchAll();
    } catch (err) { setError(err.response?.data?.detail || "Update failed"); }
  };

  const handleDelete = async (uid, name) => {
    if (!window.confirm(`Delete login for "${name}"? Their entries will remain.`)) return;
    try {
      await api.delete(`/auth/marketers/${uid}`);
      setSuccess(`Login for "${name}" deleted`);
      fetchAll();
    } catch (err) { setError(err.response?.data?.detail || "Delete failed"); }
  };

  const handleDeleteClient = async (uid, name) => {
    if (!window.confirm(`Delete client "${name}"?`)) return;
    try {
      await api.delete(`/auth/clients/${uid}`);
      setSuccess(`Client "${name}" deleted`);
      fetchAll();
    } catch (err) { setError(err.response?.data?.detail || "Delete failed"); }
  };

  const handleToggleActive = async (uid) => {
    try {
      await api.patch(`/auth/marketers/${uid}/toggle-active`);
      fetchAll();
    } catch (err) { setError(err.response?.data?.detail || "Toggle failed"); }
  };

  const handleUpdateClient = async (uid) => {
    try {
      await api.patch(`/auth/clients/${uid}`, editData);
      setEditing(null);
      setSuccess("Client updated!");
      fetchAll();
    } catch (err) { setError(err.response?.data?.detail || "Update failed"); }
  };

  const toggleProject = (pid, setter) => {
    setter(prev => ({
      ...prev,
      projects: prev.projects?.includes(pid) ? prev.projects.filter(p => p !== pid) : [...(prev.projects || []), pid],
    }));
  };

  const registered = allMarketers.filter(m => m.has_account);
  const unregistered = allMarketers.filter(m => !m.has_account);

  return (
    <div className="admin-section" data-testid="admin-section">
      <div className="section-header">
        <h2 className="section-title"><Shield size={20} color="#ff4500" /> Manage Marketers & Clients</h2>
        <div style={{display:'flex',gap:8}}>
          <button className="btn btn-sm btn-secondary" onClick={fetchAll} disabled={!loaded} data-testid="admin-refresh-btn">
            <RefreshCw size={14} className={!loaded ? 'animate-spin' : ''} /> Refresh
          </button>
          <button className="btn btn-primary" onClick={() => { setShowForm(!showForm); setShowClientForm(false); }} data-testid="add-marketer-btn">
            <UserPlus size={16} /> New Marketer
          </button>
          <button className="btn btn-secondary" onClick={() => { setShowClientForm(!showClientForm); setShowForm(false); }} data-testid="add-client-btn">
            <Building2 size={16} /> New Client
          </button>
        </div>
      </div>

      {error && <div className="alert alert-error"><X size={14}/> {error} <button onClick={() => setError("")} className="alert-close"><X size={12}/></button></div>}
      {success && <div className="alert alert-success"><Check size={14}/> {success} <button onClick={() => setSuccess("")} className="alert-close"><X size={12}/></button></div>}

      {showForm && (
        <form onSubmit={handleCreate} className="marketer-form" data-testid="marketer-form">
          <h4 style={{margin:'0 0 12px',color:'#e5e5e5',fontSize:14}}>Create New Marketer Account</h4>
          <div className="form-row">
            <input placeholder="Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})}
              required className="filter-input" data-testid="marketer-name-input" />
            <input type="email" placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
              required className="filter-input" data-testid="marketer-email-input" />
            <input type="password" placeholder="Password (min 6)" value={form.password} onChange={e => setForm({...form, password: e.target.value})}
              required minLength={6} className="filter-input" data-testid="marketer-password-input" />
          </div>
          <div className="project-chips">
            <span style={{fontSize:12,color:'#6b7280'}}>Assign projects:</span>
            {projects.map(p => (
              <button type="button" key={p.id} className={`chip ${form.projects.includes(p.id) ? 'active' : ''}`}
                onClick={() => toggleProject(p.id, setForm)}>
                <FolderKanban size={12} /> {p.display_name}
              </button>
            ))}
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={loading} data-testid="create-marketer-submit">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Create
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      {showClientForm && (
        <form onSubmit={handleCreateClient} className="marketer-form" data-testid="client-form">
          <h4 style={{margin:'0 0 12px',color:'#e5e5e5',fontSize:14}}>Create New Client Login</h4>
          <p style={{fontSize:12,color:'#6b7280',margin:'0 0 12px'}}>Clients can view project performance for their assigned projects only.</p>
          <div className="form-row">
            <input placeholder="Client Name (e.g. LogKeys)" value={clientForm.name} onChange={e => setClientForm({...clientForm, name: e.target.value})}
              required className="filter-input" data-testid="client-name-input" />
            <input type="email" placeholder="Email" value={clientForm.email} onChange={e => setClientForm({...clientForm, email: e.target.value})}
              required className="filter-input" data-testid="client-email-input" />
            <input type="password" placeholder="Password (min 6)" value={clientForm.password} onChange={e => setClientForm({...clientForm, password: e.target.value})}
              required minLength={6} className="filter-input" data-testid="client-password-input" />
          </div>
          <div className="project-chips">
            <span style={{fontSize:12,color:'#6b7280'}}>Assign projects (required):</span>
            {projects.map(p => (
              <button type="button" key={p.id} className={`chip ${clientForm.projects.includes(p.id) ? 'active' : ''}`}
                onClick={() => toggleProject(p.id, setClientForm)}>
                <FolderKanban size={12} /> {p.display_name}
              </button>
            ))}
          </div>
          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={loading} data-testid="create-client-submit">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />} Create Client
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setShowClientForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      {/* Unregistered marketers from entries */}
      {unregistered.length > 0 && (
        <div className="admin-subsection">
          <h3 className="subsection-title"><User size={16} /> Marketers from Entries <span className="badge">{unregistered.length}</span></h3>
          <p style={{fontSize:13,color:'#6b7280',margin:'0 0 16px'}}>These marketers have entries but no login account. Create one so they can access their dashboard.</p>
          <div className="table-container">
            <table className="comments-table" data-testid="unregistered-marketers-table">
              <thead><tr><th>Name</th><th>Entries</th><th>Live</th><th>Deleted</th><th>Success</th><th>Upvotes</th><th>Accounts</th><th>Action</th></tr></thead>
              <tbody>
                {unregistered.map(m => (
                  <React.Fragment key={m.name}>
                  <tr data-testid={`marketer-unreg-${m.name}`}>
                    <td><span className="marketer-cell">{m.name}</span></td>
                    <td style={{fontFamily:'JetBrains Mono'}}>{m.total}</td>
                    <td style={{color:'var(--live-green)',fontFamily:'JetBrains Mono'}}>{m.live}</td>
                    <td style={{color:'var(--deleted-red)',fontFamily:'JetBrains Mono'}}>{m.deleted}</td>
                    <td>{m.success_rate}%</td>
                    <td style={{fontFamily:'JetBrains Mono'}}>{m.upvotes}</td>
                    <td style={{fontSize:12,color:'#6b7280'}}>{m.accounts.length}</td>
                    <td>
                      {quickCreate === m.name ? (
                        <button className="btn btn-sm btn-secondary" onClick={() => setQuickCreate(null)}><X size={12} /> Cancel</button>
                      ) : (
                        <button className="btn btn-sm btn-primary" onClick={() => setQuickCreate(m.name)}
                          data-testid={`create-account-${m.name}`}>
                          <Key size={13} /> Create Login
                        </button>
                      )}
                    </td>
                  </tr>
                  {quickCreate === m.name && (
                  <tr>
                    <td colSpan={8} style={{ padding: '12px 16px', background: '#18181b' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <span style={{ fontSize: 13, color: '#9ca3af', whiteSpace: 'nowrap' }}>Create login for <strong style={{ color: '#e5e5e5' }}>{m.name}</strong>:</span>
                        <input type="email" placeholder="Email address" className="filter-input" style={{ flex: 1, maxWidth: 250 }}
                          value={quickForm.email} onChange={e => setQuickForm({...quickForm, email: e.target.value})} data-testid={`quick-email-${m.name}`} />
                        <input type="password" placeholder="Password (min 6)" className="filter-input" style={{ flex: 1, maxWidth: 200 }}
                          value={quickForm.password} onChange={e => setQuickForm({...quickForm, password: e.target.value})} data-testid={`quick-password-${m.name}`} />
                        <button className="btn btn-sm btn-primary" onClick={() => handleQuickCreate(m.name)} disabled={loading}
                          data-testid={`quick-submit-${m.name}`}>
                          {loading ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />} Create
                        </button>
                      </div>
                    </td>
                  </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Registered marketers */}
      <div className="admin-subsection">
        <h3 className="subsection-title"><Key size={16} /> Registered Marketers <span className="badge">{registered.length}</span></h3>
        <div className="table-container">
          <table className="comments-table" data-testid="registered-marketers-table">
            <thead><tr><th>Name</th><th>Email</th><th>Entries</th><th>Status</th><th>Projects</th><th>Actions</th></tr></thead>
            <tbody>
              {registered.map(m => {
                const isInactive = m.active === false;
                return (
                <tr key={m.uid} data-testid={`marketer-reg-${m.uid}`} style={isInactive ? {opacity:0.5} : {}}>
                  <td>
                    {editing === m.uid ? (
                      <input className="filter-input" value={editData.name || ""} onChange={e => setEditData({...editData, name: e.target.value})} style={{width:120}} />
                    ) : <span className="marketer-cell">{m.name}</span>}
                  </td>
                  <td className="account-cell">{m.email}</td>
                  <td style={{fontFamily:'JetBrains Mono'}}>{m.total}</td>
                  <td>
                    <button className={`btn btn-sm ${isInactive ? 'btn-danger' : 'btn-secondary'}`}
                      onClick={() => handleToggleActive(m.uid)} data-testid={`toggle-active-${m.uid}`}
                      title={isInactive ? 'Click to activate' : 'Click to deactivate'}>
                      <Power size={12} /> {isInactive ? 'Inactive' : 'Active'}
                    </button>
                  </td>
                  <td>
                    {editing === m.uid ? (
                      <div className="project-chips" style={{flexWrap:'wrap'}}>
                        {projects.map(p => (
                          <button type="button" key={p.id}
                            className={`chip ${(editData.projects||[]).includes(p.id) ? 'active' : ''}`}
                            onClick={() => toggleProject(p.id, setEditData)}>
                            {p.display_name}
                          </button>
                        ))}
                      </div>
                    ) : (m.assigned_projects || []).map(pid => {
                      const proj = projects.find(p => p.id === pid);
                      return <span key={pid} className="chip active" style={{margin:'2px'}}>{proj?.display_name || pid}</span>;
                    })}
                  </td>
                  <td>
                    {editing === m.uid ? (
                      <div style={{display:'flex',gap:4}}>
                        <button className="btn btn-sm btn-primary" onClick={() => handleUpdate(m.uid)}><Check size={14} /></button>
                        <button className="btn btn-sm btn-secondary" onClick={() => setEditing(null)}><X size={14} /></button>
                      </div>
                    ) : (
                      <div style={{display:'flex',gap:4}}>
                        <button className="btn btn-sm btn-secondary" onClick={() => { setEditing(m.uid); setEditData({name:m.name,projects:m.assigned_projects||[]}); }}>
                          <Edit2 size={14} /></button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(m.uid, m.name)}><Trash2 size={14} /></button>
                      </div>
                    )}
                  </td>
                </tr>
                );
              })}
              {registered.length === 0 && <tr><td colSpan={6} style={{textAlign:'center',color:'#6b7280',padding:32}}>No registered marketers yet. Create accounts above.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Clients */}
      <div className="admin-subsection">
        <h3 className="subsection-title"><Building2 size={16} /> Client Accounts <span className="badge">{clients.length}</span></h3>
        <p style={{fontSize:13,color:'#6b7280',margin:'0 0 16px'}}>Clients can only view performance data for their assigned projects.</p>
        <div className="table-container">
          <table className="comments-table" data-testid="clients-table">
            <thead><tr><th>Name</th><th>Email</th><th>Projects</th><th>Created</th><th>Actions</th></tr></thead>
            <tbody>
              {clients.map(c => (
                <tr key={c.uid} data-testid={`client-${c.uid}`}>
                  <td>
                    {editing === c.uid ? (
                      <input className="filter-input" value={editData.name || ""} onChange={e => setEditData({...editData, name: e.target.value})} style={{width:120}} />
                    ) : <span className="marketer-cell"><Building2 size={12} /> {c.name}</span>}
                  </td>
                  <td className="account-cell">{c.email}</td>
                  <td>
                    {editing === c.uid ? (
                      <div className="project-chips" style={{flexWrap:'wrap'}}>
                        {projects.map(p => (
                          <button type="button" key={p.id}
                            className={`chip ${(editData.projects||[]).includes(p.id) ? 'active' : ''}`}
                            onClick={() => toggleProject(p.id, setEditData)}>
                            {p.display_name}
                          </button>
                        ))}
                      </div>
                    ) : (c.projects || []).map(pid => {
                      const proj = projects.find(p => p.id === pid);
                      return <span key={pid} className="chip active" style={{margin:'2px'}}>{proj?.display_name || pid}</span>;
                    })}
                  </td>
                  <td style={{fontSize:12,color:'#6b7280'}}>{c.created_at?.split('T')[0]}</td>
                  <td>
                    {editing === c.uid ? (
                      <div style={{display:'flex',gap:4}}>
                        <button className="btn btn-sm btn-primary" onClick={() => handleUpdateClient(c.uid)}><Check size={14} /></button>
                        <button className="btn btn-sm btn-secondary" onClick={() => setEditing(null)}><X size={14} /></button>
                      </div>
                    ) : (
                      <div style={{display:'flex',gap:4}}>
                        <button className="btn btn-sm btn-secondary" onClick={() => { setEditing(c.uid); setEditData({name:c.name,projects:c.projects||[]}); }}
                          data-testid={`edit-client-${c.uid}`}>
                          <Edit2 size={14} />
                        </button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDeleteClient(c.uid, c.name)}
                          data-testid={`delete-client-${c.uid}`}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
              {clients.length === 0 && <tr><td colSpan={5} style={{textAlign:'center',color:'#6b7280',padding:32}}>No client accounts yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
